import { useRouter } from 'next/router'
import NextLink from 'next/link'

import { Text } from '@wartek-id/text-v1'
import { Spinner } from '@wartek-id/spinner'
import { Separator } from '@wartek-id/separator'
import { Button } from '@wartek-id/button-v1'
import { Link } from '@wartek-id/link'

import PanelPerilakuKerja from 'app/KinerjaPage/components/PanelPerilakuKerja'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { useAPIKinerja, useAPIKinerjaProfile } from 'api/kinerja/useAPIKinerja'
import useSKP from 'app/KinerjaPage/hooks/useSKP'
import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import ContentProvider from 'app/KinerjaPage/components/ContentProvider'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'

import type { Jenjang, MultiPeriodParams } from 'types/kinerja'

export type PerilakuKerjaProps = {
  isViewOnly?: boolean
  panduanUrl?: string
  periodParams?: MultiPeriodParams
}
const PerilakuKerja = ({
  isViewOnly,
  panduanUrl = `${PENGELOLAAN_KINERJA_GURU_PATH}/panduan/perilaku-kerja`,
  periodParams,
}: PerilakuKerjaProps) => {
  const { push } = useRouter()
  const { getAspekPerilakuKerjaById } = useContentContext()
  const { data: { data: skp } = {}, isFetched: isSKPFetched } = useAPIKinerja(
    {
      staleTime: 60 * 60 * 1000,
    },
    periodParams
  )
  const { getChangesPerilakuKerja } = useSKP(skp)

  const backToHome = () => {
    push(PENGELOLAAN_KINERJA_GURU_PATH)
  }

  return (
    <div className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
      <div className="max-w-7xl mx-auto">
        <Header
          backTestId="icon-back"
          className="mb-9"
          title="Perilaku Kerja"
        />

        <div className="mt-12 bg-white rounded-lg">
          <div className="p-9">
            <div className="space-y-1.5">
              <Text variant="heading-md" as="h2">
                Daftar Perilaku Kerja Pilihan Anda
              </Text>
              <Text className="mt-1.5">
                Anda tidak perlu kumpulkan Bukti Dukung. Cukup laksanakan dalam
                keseharian Anda di luar sistem.
              </Text>
              <NextLink href={panduanUrl} passHref>
                <Link className="font-bold !pl-0 block">
                  Cek Daftar Perilaku Kinerja.
                </Link>
              </NextLink>
            </div>

            {!isSKPFetched ? (
              <div className="mt-9 flex justify-center">
                <Spinner data-testid="loading-spinner" />
              </div>
            ) : (
              <div className="mt-9 flex flex-col gap-9">
                {skp.reviewPrilakuKerja.map((perilakuKerja) => {
                  const aspek = getAspekPerilakuKerjaById(perilakuKerja.aspekId)
                  const {
                    originalIndikator,
                    indikator,
                    originalPerwujudanPerilaku,
                    perwujudanPerilaku,
                  } = getChangesPerilakuKerja(perilakuKerja)

                  return (
                    <PanelPerilakuKerja
                      key={`PanelPerilakuKerja-${aspek.id}`}
                      data-testid={`PanelPerilakuKerja-${aspek.id}`}
                      title={aspek?.value}
                      originalIndikatorPerilaku={originalIndikator}
                      indikatorPerilaku={indikator}
                      originalPerwujudanPerilaku={originalPerwujudanPerilaku}
                      perwujudanPerilaku={perwujudanPerilaku}
                    />
                  )
                })}
              </div>
            )}
          </div>

          <Separator />

          {!isViewOnly && (
            <div className="px-8 py-4 flex gap-5 justify-end">
              <Button size="lg" onClick={() => backToHome()} type="button">
                Saya mengerti
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const WrappedPerilakuKerja = ({
  isViewOnly,
  panduanUrl,
  periodParams,
}: PerilakuKerjaProps) => {
  const { data: responseGetKinerjaProfile } = useAPIKinerjaProfile()

  return (
    <ContentProvider
      jenjang={responseGetKinerjaProfile?.data.jenjang as Jenjang}
      periodParams={periodParams}
    >
      <PerilakuKerja
        isViewOnly={isViewOnly}
        panduanUrl={panduanUrl}
        periodParams={periodParams}
      />
    </ContentProvider>
  )
}

export default WrappedPerilakuKerja
